import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {loadPartnerProjectsWithNew, selectProject} from "../../../../actions/projectsActions";
import {deleteSmartLink, loadPartnerSmartLink, loadSmartLinkUrl} from "../../../../actions/partnerSmartLinkActions";
import {showWarning} from "../../../../actions/warningActions";
import SmartLinkEditingTable from "./SmartLinkEditingTable";
import MaterialUISelect from "../../../common/selects/MaterialUISelect";
import ProjectCreationCollapse from "../../../common/projectCreation/ProjectCreationCollapse";
import PropTypes from "prop-types";
import PageContent from "../../../common/page/PageContent";
import {billingTypesData} from "../../../../utils/offerUtils";
import SmartLinkEditingCheckbox from "./SmartLinkEditingCheckbox";
import SmartLinkEditingInput from "./SmartLinkEditingInput";
import IconPopover from "../../../common/popovers/IconPopver";

const SMART_LINK_HIDING_POPOVER_CONTENT = `Вы можете на время скрыть отображение лендингов смартлинка и перенаправлять абонентов на свою ссылку.
                                    Реальные лендинги будут отображаться только после исчерпания кликов, указанных во втором поле.`;

function getInitialWidth() {
    return typeof window === 'undefined' ? 0 : window.innerWidth;
}

function getContentWidth(container) {
    if (container && container.offsetWidth) {
        return container.offsetWidth;
    }

    return getInitialWidth();
}

function ReturnButton({onClick}) {
    return (
        <button className="circle-btn" title="Назад"
                onClick={onClick}>
            <i className="ti ti-back-left"/>
        </button>
    );
}

function HidingLandingTitle() {
    return (
        <p className="title-smart">Скрытие ЛП &nbsp;
            <IconPopover
                displayedContent={SMART_LINK_HIDING_POPOVER_CONTENT}
                placement="top" iconClassName="ti ti-help-alt" target={"PopoverHelpIcon2"}/>
        </p>
    );
}

function DesktopHidingLandingFields() {
    return (
        <div className="smart-link-editing-hiding-fields">
            <div className="padding5">
                <HidingLandingTitle/>
            </div>
            <div className="smart-link-editing-hiding-inputs">
                <div className="smart-link-editing-hiding-url">
                    <SmartLinkEditingInput name="fakeLandingUrl"
                                           type="url"
                                           placeholder="Ссылка для перенаправления"/>
                </div>
                <div className="smart-link-editing-hiding-clicks">
                    <SmartLinkEditingInput name="availableFakeClicks"
                                           type="number"
                                           placeholder="Количество кликов"/>
                </div>
            </div>
        </div>
    );
}

function MobileHidingLandingFields() {
    return (
        <>
            <HidingLandingTitle/>
            <div className="smart-link-editing-mobile-hiding-inputs">
                <SmartLinkEditingInput name="fakeLandingUrl"
                                       type="url"
                                       placeholder="Ссылка для перенаправления"/>
                <SmartLinkEditingInput name="availableFakeClicks"
                                       type="number"
                                       placeholder="Количество кликов"/>
            </div>
        </>
    );
}

function DesktopSmartLinkOptions({smartLink}) {
    return (
        <>
            <div className="smart-link-editing-option">
                <SmartLinkEditingCheckbox name="maxBenefit" label="Максимум пользы"
                                          title="Абоненты с неподходящими IP адресами будут перенаправлены на специальные офферы"
                                          width="170px"/>
            </div>
            {!smartLink?.autoRotation && <div className="smart-link-editing-option">
                <SmartLinkEditingCheckbox name="autoProbability" label="Автоматический вес"
                                          title="Вес офферов будет определяться автоматически в зависимости от CR"
                                          width="180px"/>
            </div>}
        </>
    );
}

function MobileSmartLinkOptions({smartLink}) {
    return (
        <div className="smart-link-editing-mobile-options">
            {!smartLink?.autoRotation &&
            <SmartLinkEditingCheckbox smartLink={smartLink} name="autoProbability"
                                      label='Автоматический вес' width="180px"/>}
            <SmartLinkEditingCheckbox smartLink={smartLink} name="maxBenefit" label='Максимум пользы'
                                      width="180px"/>

        </div>
    );
}

function DesktopSmartLinkActions({smartLink, canBeEdited, isFormExpanded, onChangeSmartLinkOffers, onSaveSmartLink}) {
    if (isFormExpanded || !canBeEdited) {
        return null;
    }

    return (
        <div className="select-potok smart-link-editing-actions">
            {!smartLink?.autoRotation &&
            <button className="btn btn-outline-primary btn-margin select-potok"
                    onClick={onChangeSmartLinkOffers}>Изменить набор офферов
            </button>}
            <button className="btn btn-outline-success btn-margin"
                    onClick={onSaveSmartLink}>Сохранить
            </button>
        </div>
    );
}

function MobileSmartLinkActions({smartLink, canBeEdited, isFormExpanded, onChangeSmartLinkOffers, onSaveSmartLink}) {
    if (isFormExpanded || !canBeEdited) {
        return null;
    }

    return (
        <div className="buttons-block btn-block smart-link-editing-mobile-actions">
            {!smartLink?.autoRotation && <button className="btn btn-outline-primary"
                                                 onClick={onChangeSmartLinkOffers}>Выбрать офферы
            </button>}
            <button className="btn btn-outline-success"
                    onClick={onSaveSmartLink}>Сохранить
            </button>
        </div>
    );
}


function SmartLinkEditingComponent({
    smartLinkId,
    returnToPrevComponent: returnToPrevComponentProp,
    selectedProject,
    onSelectProject,
    showSmartLinkOffersChangingContent,
    saveSmartLink,
    selectedBillingType,
    onSelectBillingType,
}) {
    const dispatch = useDispatch();
    const projects = useSelector(state => state.projects.projects);
    const smartLink = useSelector(state => state.partnerSmartLinks.editableSmartLink);
    const smartLinkUrl = useSelector(state => state.partnerSmartLinks.editableSmartLinkUrl);
    const domains = useSelector(state => state.partnerDomainsReducer.partnerDomains);

    const containerRef = useRef(null);
    const [isFormExpanded, setIsFormExpanded] = useState(false);
    const [isProjectSelected, setIsProjectSelected] = useState(false);
    const [width, setWidth] = useState(getInitialWidth);
    const [selectedDomain, setSelectedDomain] = useState(null);

    useLayoutEffect(() => {
        const handleResize = () => {
            setWidth(prevWidth => {
                const currentWidth = getContentWidth(containerRef.current);
                return currentWidth !== prevWidth ? currentWidth : prevWidth;
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        dispatch(loadPartnerProjectsWithNew());
        if (!smartLink || smartLink.id !== smartLinkId) {
            dispatch(loadPartnerSmartLink(smartLinkId));
        }
        if (smartLink) {
            dispatch(loadSmartLinkUrl(smartLink.id, smartLink.projectId));
        }
    }, []);

    useEffect(() => {
        if (domains && !selectedDomain) {
            const defaultDomain = domains.find(domain => domain.defaultDomain);
            setSelectedDomain(defaultDomain);
        }
    }, [domains, selectedDomain]);

    useEffect(() => {
        if (smartLink) {
            const billingType = smartLink.smartLinkOffers?.[0].billingType;
            const billingTypeDataItem = billingType && billingTypesData.find(dataItem => dataItem.type === billingType);
            onSelectBillingType(billingTypeDataItem);
        }
    }, [smartLink, onSelectBillingType]);

    const returnToPrevComponent = () => {
        if (!smartLink) {
            returnToPrevComponentProp();
            return;
        }
        if (smartLink.hasChanges) {
            saveSmartLink("Смартлинк изменён. Выйти с сохранением?",
                () => returnToPrevComponentProp());
        } else {
            returnToPrevComponentProp();
        }
        if (!selectedProject || selectedProject.id === -1) {
            dispatch(selectProject(projects[0]))
        }
    };

    const changeSmartLinkOffers = () => {
        if (smartLink && smartLink.smartLinkOffers) {
            showSmartLinkOffersChangingContent(smartLink);
        }
    };

    const handleSelectProject = (project) => {
        if (!smartLink) {
            return;
        }
        if (project && project.id !== -1 && project.id !== smartLink.projectId && smartLink.status !== "GLOBAL") {
            smartLink.hasChanges = true;
        }
        const isExpanded = project && project.id === -1;
        setIsFormExpanded(isExpanded);
        setIsProjectSelected(true);
        onSelectProject(project);
        if (!isExpanded) {
            project && dispatch(loadSmartLinkUrl(smartLink.id, project.id));
        }
    };

    const hideForm = () => {
        setIsFormExpanded(false);
        setIsProjectSelected(false);
    };

    const onCreateProject = (createdProject) => {
        if (!smartLink) {
            return;
        }
        dispatch(loadSmartLinkUrl(smartLink.id, createdProject.id));
    };

    const handleDeleteSmartLink = (smartLink) => {
        dispatch(showWarning("Удалить смарлинк?", () => dispatch(deleteSmartLink(smartLink, returnToPrevComponentProp))));
    };

    const canBeEdited = smartLink && smartLink.status !== "GLOBAL";
    const smartLinkProject = isProjectSelected ? selectedProject : ((projects && smartLink) ? projects.find(project => project.id === smartLink.projectId) : null);

    const projectsFilter =
        <MaterialUISelect
            styles={{root: {minWidth: 180}}}
            dropdownData={projects}
            displayedFields={['sourceId', 'name']} dataField='id'
            placeholder="Выберите поток"
            selectedValue={smartLinkProject}
            onSelectCallbackFunction={(project) => handleSelectProject(project)}
            isSearchable={true}/>;

    const domainsFilter = domains && domains.length > 0
        && <MaterialUISelect
            styles={{root: {minWidth: 180}}}
            dropdownData={domains}
            displayedFields={['id', 'cname']} dataField='id'
            placeholder="Выбрать домен"
            selectedValue={selectedDomain}
            onSelectCallbackFunction={(domain) => setSelectedDomain(domain)}
            isSearchable={true}/>;

    const billingTypeInput =
        selectedBillingType && <MaterialUISelect
            styles={{root: {minWidth: 120}}}
            dropdownData={[]}
            displayedField="fullName" dataField='type'
            placeholder="Тип выплаты"
            selectedValue={selectedBillingType}
            isDisabled/>;

    const returnBtn = <ReturnButton onClick={returnToPrevComponent}/>;
    const saveSmartLinkWithConfirm = () => saveSmartLink("Сохранить изменения?");

    const desktopForm =
        <div className="smart-link-editing-form">
            <div className="smart-link-editing-toolbar">
                <div className="smart-link-editing-controls">
                    <div className="smart-link-editing-control">
                        {projectsFilter}
                    </div>
                    <div className="smart-link-editing-control">
                        {domainsFilter}
                    </div>
                    <div className="smart-link-editing-control">
                        {billingTypeInput}
                    </div>
                    <DesktopSmartLinkOptions smartLink={smartLink}/>
                    <DesktopSmartLinkActions smartLink={smartLink}
                                             canBeEdited={canBeEdited}
                                             isFormExpanded={isFormExpanded}
                                             onChangeSmartLinkOffers={changeSmartLinkOffers}
                                             onSaveSmartLink={saveSmartLinkWithConfirm}/>
                </div>
                <div className="smart-link-editing-return">
                    {returnBtn}
                </div>
            </div>
            <DesktopHidingLandingFields/>
        </div>;

    const mobileForm =
        <div className="smart-link-editing-mobile-form">
            <div className="smart-link-editing-mobile-return">
                {returnBtn}
            </div>
            <div className="smart-link-editing-mobile-control">
                {projectsFilter}
            </div>
            <div className="smart-link-editing-mobile-control">
                {domainsFilter}
            </div>
            <div className="smart-link-editing-mobile-control">
                {billingTypeInput}
            </div>
            <MobileSmartLinkOptions smartLink={smartLink}/>
            <MobileHidingLandingFields/>
            <MobileSmartLinkActions smartLink={smartLink}
                                    canBeEdited={canBeEdited}
                                    isFormExpanded={isFormExpanded}
                                    onChangeSmartLinkOffers={changeSmartLinkOffers}
                                    onSaveSmartLink={saveSmartLinkWithConfirm}/>
        </div>;


    const tables = <SmartLinkEditingTable smartLinkUrl={smartLinkUrl}
                                          onDelete={handleDeleteSmartLink}
                                          selectedDomain={selectedDomain}/>;
    return (
        <PageContent
            contentEl={
                <div className="background-wht-blk smart-link-editing">
                    {width > 659 ? desktopForm : mobileForm}
                    <ProjectCreationCollapse isExpanded={isFormExpanded}
                                             onCreateProjectCallbackFunction={onCreateProject}
                                             onHideForm={hideForm}
                                             onSelectCreatedProject={handleSelectProject}/>
                    {tables}
                    <div ref={containerRef}/>
                </div>
            }
        />

    );
}

SmartLinkEditingComponent.propTypes = {
    smartLinkId: PropTypes.any,
    displayedComponent: PropTypes.string,
    displayComponent: PropTypes.func,
    returnToPrevComponent: PropTypes.func,
    selectedProject: PropTypes.any,
    onSelectProject: PropTypes.func,
    onExcludeOffer: PropTypes.func,
    onShowEditSmartLinkComponent: PropTypes.func,
    deleteSmartLink: PropTypes.func,
    showSmartLinkOffersChangingContent: PropTypes.func,
    saveSmartLink: PropTypes.func,
    selectedBillingType: PropTypes.any,
    onSelectBillingType: PropTypes.func,
};

export default SmartLinkEditingComponent;
